/**
 * Live settings (`/rules/live`): every rule of each company with its mode and source, read
 * only (docs/goa-integration-concept.md sections 2.9 and 3.4). The managers' tab of
 * "Rules and countries"; it was the separate page "Entity rules" before.
 *
 * For people holding `see_rules` (managers and admins, the companies of their areas).
 * One tab per company: its settings (default language, freight model), then every rule
 * with its stage, mode (block, warn, note, off), a setting where the rule has one (the
 * margin threshold only for managers who see the margin), its data tables and the GoA
 * source. A data table opens in a dialog. Sales see the reasons on their cases and the
 * explanations in the other tabs; the server is the gate.
 *
 * Notes from loading the rules (for example a freight band that charges less than the
 * band below) are shown inside the tab of their company, in business words; the data
 * file they come from is behind "Source files". The server sends only the notes of the
 * viewer's companies. A note about the whole rule set stays above the tabs.
 *
 * Nothing here changes a rule: rules are changed by IT on request.
 */

import { Icon, Scale } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapsible,
  DataGrid,
  type DataGridColumnDef,
  DescriptionList,
  EmptyState,
  Freshness,
  Skeleton,
  Tabs,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { HelpButton } from "@/features/help";
import { type ApiError, type EntityRulesView, type RuleRow, useMe, useRuleSet } from "@/lib/api";
import { hasCapability } from "@/lib/areas";
import { logger } from "@/lib/logger";
import {
  dataRowsText,
  entityLabel,
  FREIGHT_MODEL_LABEL,
  type LoadNote,
  MODE_META,
  type RuleMode,
  STAGE_LABEL,
  toLoadNotes,
} from "@/lib/rules";

import { RuleTableDialog } from "./rule-table-dialog";

/** The tab title (quoted by the help). */
export const LIVE_SETTINGS_TITLE = "Live settings";
/** The message for a person without `see_rules`. */
export const RULES_NOT_ALLOWED =
  "The live settings are for managers and admins. Sales see the reasons on each case and the explanations in the other tabs.";
/** The line under the title: who changes the rules (quoted by the help). */
export const RULES_CHANGED_BY =
  "Rules are changed by IT on request. This page shows the active set.";

const MODES: readonly RuleMode[] = ["block", "warn", "info", "off"];

function failureText(error: unknown): string {
  const status = (error as ApiError | null)?.status;
  return status === 403
    ? RULES_NOT_ALLOWED
    : "I2Q cannot read the rules now. Try again in a moment.";
}

export function ModeBadge({ mode }: { mode: RuleMode }) {
  const meta = Object.hasOwn(MODE_META, mode) ? MODE_META[mode] : MODE_META.warn;
  return (
    <Badge tone={meta.tone} size="sm" title={meta.text} data-mode={mode}>
      {meta.label}
    </Badge>
  );
}

function ModeLegend() {
  return (
    <ul className="flex flex-col gap-1.5" aria-label="What the modes mean">
      {MODES.map((mode) => (
        <li key={mode} className="flex items-start gap-2">
          <ModeBadge mode={mode} />
          <span className="text-fg-muted text-xs break-words">{MODE_META[mode].text}</span>
        </li>
      ))}
    </ul>
  );
}

/** The title over the load notes of a company. */
export const LOAD_NOTES_TITLE = "Data check pending (IT)";
/** The disclosure with the data files behind the notes. */
export const SOURCE_FILES_LABEL = "Source files";

/** Load notes in business words; the data files behind a collapsed "Source files". */
function LoadNotes({ notes, title }: { notes: LoadNote[]; title: string }) {
  const [open, setOpen] = useState(false);
  if (notes.length === 0) {
    return null;
  }
  const files = [...new Set(notes.map((note) => note.source).filter((f): f is string => !!f))];
  return (
    <Alert tone="warning" title={title}>
      <ul className="flex flex-col gap-0.5">
        {notes.map((note, index) => (
          <li key={`${note.text}-${index}`} className="text-xs break-words">
            {note.text}
          </li>
        ))}
      </ul>
      {files.length > 0 && (
        <Collapsible.Root
          open={open}
          onOpenChange={(next) => {
            logger.debug("[rules] source files", { open: next });
            setOpen(next);
          }}
          className="mt-1"
        >
          <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
            {SOURCE_FILES_LABEL} ({files.length})
          </Collapsible.Trigger>
          <Collapsible.Panel>
            <ul className="mt-1 flex flex-col gap-0.5">
              {files.map((file) => (
                <li key={file} className="text-fg-muted font-mono text-xs break-all">
                  {file}
                </li>
              ))}
            </ul>
          </Collapsible.Panel>
        </Collapsible.Root>
      )}
    </Alert>
  );
}

/** The data-table fields the server may add to a rule row (absent on an older backend). */
type RuleRowScope = RuleRow & { data_entities?: string[] | null };

function EntityRules({
  view,
  notes,
  entityName,
  onOpenTable,
}: {
  view: EntityRulesView;
  /** The load notes of this company. */
  notes: LoadNote[];
  /** Company code to name, for "Applies to United Kingdom only". */
  entityName: (code: string) => string;
  onOpenTable: (table: string) => void;
}) {
  const rules = view.rules ?? [];
  const columns = useMemo<DataGridColumnDef<RuleRow>[]>(
    () => [
      {
        id: "rule",
        header: "Rule",
        cell: (row) => (
          <span className="flex min-w-0 flex-col whitespace-normal">
            <span className="text-fg text-sm font-semibold break-words">{row.title}</span>
            <span className="text-fg-subtle font-mono text-xs break-all">{row.rule_id}</span>
          </span>
        ),
      },
      {
        id: "stage",
        header: "Stage",
        width: 140,
        cell: (row) => (
          <span className="text-fg-muted text-xs whitespace-normal">
            {Object.hasOwn(STAGE_LABEL, row.stage) ? STAGE_LABEL[row.stage] : row.stage}
          </span>
        ),
      },
      {
        id: "mode",
        header: "Mode",
        width: 80,
        cell: (row) => <ModeBadge mode={row.mode} />,
      },
      {
        id: "value",
        header: "Setting",
        width: 96,
        cell: (row) =>
          row.value ? (
            <span className="text-fg text-xs tabular-nums">{row.value}</span>
          ) : (
            <span className="text-fg-subtle text-xs">-</span>
          ),
      },
      {
        id: "data",
        header: "Data",
        width: 200,
        cell: (row) => {
          const tables = row.data_tables ?? [];
          if (tables.length === 0) {
            return <span className="text-fg-subtle text-xs">-</span>;
          }
          return (
            <span className="flex min-w-0 flex-col items-start gap-0.5">
              {tables.map((table) => (
                <Button
                  key={table}
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  className="h-auto px-0 py-0 font-mono text-xs"
                  aria-label={`Show the table ${table}`}
                  onClick={() => onOpenTable(table)}
                >
                  {table}
                </Button>
              ))}
              {row.data_rows != null && (
                <span className="text-fg-subtle text-xs whitespace-normal tabular-nums">
                  {dataRowsText(
                    row.data_rows,
                    ((row as RuleRowScope).data_entities ?? [])
                      .filter((code) => code !== view.entity)
                      .map(entityName),
                  )}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "source",
        header: "Source",
        cell: (row) => (
          <span
            className="text-fg-muted line-clamp-3 text-xs break-words whitespace-normal"
            title={row.source}
          >
            {row.source}
          </span>
        ),
      },
    ],
    [onOpenTable, entityName, view.entity],
  );

  return (
    <div className="flex flex-col gap-4">
      <LoadNotes notes={notes} title={LOAD_NOTES_TITLE} />
      <DescriptionList
        orientation="horizontal"
        columns={3}
        items={[
          { label: "Default language", value: view.default_language || "-" },
          {
            label: "Freight model",
            value: Object.hasOwn(FREIGHT_MODEL_LABEL, view.freight_model)
              ? FREIGHT_MODEL_LABEL[view.freight_model]
              : view.freight_model,
          },
          ...(view.tier_currency ? [{ label: "Freight currency", value: view.tier_currency }] : []),
        ]}
      />
      <div className="overflow-x-auto">
        <DataGrid<RuleRow>
          columns={columns}
          data={rules}
          getRowId={(row) => row.rule_id}
          density="compact"
          tableLayout="fixed"
          className="min-w-[960px]"
          aria-label={`Rules of ${entityLabel(view.entity, view.label)}`}
        />
      </div>
    </div>
  );
}

/** One autonomy switch as GET /rules lists it (read only). */
export interface AutonomySwitchRow {
  name: string;
  label: string;
  on?: boolean;
}

/** The switch names whose rule closes or files inquiries by itself, in this order. */
const CLOSE_SWITCHES = [
  "autonomy_close_no_activity",
  "autonomy_close_answered",
  "autonomy_close_desk_deleted",
  "autonomy_replied_from_fo",
];

/** What the no-activity close does, in the words sales and managers read everywhere. */
export const NO_ACTIVITY_LIVE_TEXT =
  "An inquiry gets the status No activity after 21 days without a take, a status, a confirmed customer or a reply. It then leaves To review and the open counts. It is closed in I2Q only; nothing changes in Outlook or F&O. Reopen brings it back for another 21 days.";

/** "What I2Q does on its own": the close switches with their state (read only). */
export function AutonomyCloseSettings({ switches }: { switches: AutonomySwitchRow[] }) {
  const rows = CLOSE_SWITCHES.map((name) => switches.find((row) => row.name === name)).filter(
    (row): row is AutonomySwitchRow => row !== undefined,
  );
  if (rows.length === 0) {
    return null;
  }
  return (
    <Card variant="outline">
      <Card.Body className="flex flex-col gap-2" data-autonomy-close>
        <Typography variant="label">What I2Q closes on its own</Typography>
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.name} className="flex flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={row.on ? "success" : "neutral"} variant="subtle" size="sm">
                  {row.on ? "On" : "Off"}
                </Badge>
                <Typography as="span" variant="body-sm" className="text-fg">
                  {row.label}
                </Typography>
              </span>
              {row.name === "autonomy_close_no_activity" && (
                <Typography variant="caption" tone="muted">
                  {NO_ACTIVITY_LIVE_TEXT}
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </Card.Body>
    </Card>
  );
}

/** True when the session may see the live settings (managers and admins). */
export function useCanSeeLiveSettings(): boolean {
  const { data: me } = useMe();
  return hasCapability(me, "see_rules");
}

export function LiveSettingsPage() {
  const canSee = useCanSeeLiveSettings();
  const ruleSet = useRuleSet(canSee);
  const [params, setParams] = useSearchParams();
  const [openTable, setOpenTable] = useState<string | null>(null);

  if (!canSee) {
    return (
      <EmptyState
        icon={<Icon icon={Scale} />}
        title="Not for your role"
        description={RULES_NOT_ALLOWED}
      />
    );
  }

  const data = ruleSet.data;
  const entities = data?.entities ?? [];
  const asked = (params.get("entity") ?? "").toLowerCase();
  const current = entities.find((view) => view.entity === asked) ?? entities[0] ?? null;
  const notes = toLoadNotes(data?.warnings);
  const known = new Set(entities.map((view) => view.entity));
  // A note of a company the viewer does not see is never shown (the server filters too).
  const generalNotes = notes.filter((note) => note.entity === null);
  const notesOf = (entity: string) => notes.filter((note) => note.entity === entity);
  const nameOf = (code: string) => {
    const view = entities.find((entry) => entry.entity === code);
    return entityLabel(code, view?.label);
  };
  logger.debug("[rules] load notes", {
    total: notes.length,
    general: generalNotes.length,
    hidden: notes.filter((note) => note.entity !== null && !known.has(note.entity)).length,
  });

  const choose = (entity: string) => {
    logger.debug("[rules] entity", entity);
    setParams(
      (next) => {
        next.set("entity", entity);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Typography as="h2" variant="heading-5">
          {LIVE_SETTINGS_TITLE}
        </Typography>
        <Badge tone="neutral" variant="subtle" size="sm">
          Managers and admins
        </Badge>
        <div className="ml-auto flex items-center gap-3">
          {data && <Freshness label="Loaded" since={data.loaded_at} tone="neutral" />}
          <HelpButton topic="rules" />
        </div>
      </div>
      <Typography variant="body-sm" tone="muted">
        {data ? `Rule set ${data.version}. ` : ""}
        {RULES_CHANGED_BY}
      </Typography>

      {ruleSet.isLoading ? (
        <Skeleton variant="rect" height={240} />
      ) : ruleSet.isError ? (
        <Card variant="outline">
          <Typography variant="body-sm" tone="muted" role="alert" className="p-4">
            {failureText(ruleSet.error)}
          </Typography>
        </Card>
      ) : !current ? (
        <EmptyState
          icon={<Icon icon={Scale} />}
          title="No company"
          description="None of your areas has entity rules yet."
        />
      ) : (
        <>
          <LoadNotes notes={generalNotes} title="Notes from loading the rules" />
          <Card variant="outline" padding="none">
            <Card.Body className="flex flex-col gap-4">
              <Tabs.Root
                value={current.entity}
                onValueChange={(value) => choose(String(value))}
                className="flex min-w-0 flex-col gap-4"
              >
                <div className="-mb-1 overflow-x-auto pb-1">
                  <Tabs.List aria-label="Companies" className="w-max min-w-full">
                    {entities.map((view) => (
                      <Tabs.Tab key={view.entity} value={view.entity} className="shrink-0">
                        <span className="whitespace-nowrap">
                          {entityLabel(view.entity, view.label)}
                        </span>
                      </Tabs.Tab>
                    ))}
                    <Tabs.Indicator />
                  </Tabs.List>
                </div>
                {entities.map((view) => (
                  <Tabs.Panel key={view.entity} value={view.entity} className="min-w-0">
                    {view.entity === current.entity && (
                      <EntityRules
                        view={view}
                        notes={notesOf(view.entity)}
                        entityName={nameOf}
                        onOpenTable={setOpenTable}
                      />
                    )}
                  </Tabs.Panel>
                ))}
              </Tabs.Root>
            </Card.Body>
          </Card>
          <AutonomyCloseSettings switches={data?.autonomy ?? []} />
          <Card variant="outline">
            <Card.Body className="flex flex-col gap-2">
              <Typography variant="label">What the modes mean</Typography>
              <ModeLegend />
            </Card.Body>
          </Card>
          <RuleTableDialog
            entity={current.entity}
            entityName={entityLabel(current.entity, current.label)}
            table={openTable}
            onClose={() => setOpenTable(null)}
          />
        </>
      )}
    </div>
  );
}
